import { z } from "zod";
import type { DocumentTypeConfig } from "../types";

/**
 * French occupational medicine certificate types.
 *
 * - SIR: Suivi Individuel Renforcé (reinforced individual monitoring, for workers exposed to particular risks)
 * - SIA: Suivi Individuel Adapté (adapted monitoring, e.g. night workers, disabled workers, etc.)
 * - VIPI: Visite d'Information et de Prévention Initiale (initial prevention visit at hiring)
 */
export const MedicalCertificateTypeEnum = z.enum(["SIR", "SIA", "VIPI"]);

export const MedicalCertificateDataSchema = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  certificate_type: MedicalCertificateTypeEnum,
  aptitude_opinion_date: z.string(),
  next_visit_date: z.string(),
});

export type MedicalCertificateData = z.infer<typeof MedicalCertificateDataSchema>;

export const MedicalCertificateExtractionSchema = z.object({
  isValidDocumentType: z.boolean(),
  reason: z.string().optional(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  certificate_type: MedicalCertificateTypeEnum.nullable(),
  aptitude_opinion_date: z.string().nullable(),
  next_visit_date: z.string().nullable(),
});

export type MedicalCertificateExtraction = z.infer<typeof MedicalCertificateExtractionSchema>;

export const MedicalCertificateContextSchema = z.object({
  talentId: z.guid(),
  medicalCheckupRequestId: z.guid().optional(),
});

export type MedicalCertificateContext = z.infer<typeof MedicalCertificateContextSchema>;

type MedicalCertificateConfig = DocumentTypeConfig<
  typeof MedicalCertificateDataSchema,
  typeof MedicalCertificateContextSchema
>;

export const MEDICAL_CERTIFICATE_CONFIG: MedicalCertificateConfig = {
  documentType: "medical_certificate",
  documentName: "Certificat médical",
  maxFiles: 1,
  acceptedMimeTypes: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
  dataSchema: MedicalCertificateDataSchema,
  contextSchema: MedicalCertificateContextSchema,
  requiredFields: ["certificate_type", "aptitude_opinion_date", "next_visit_date"],
  fieldLabels: {
    first_name: "Prénom",
    last_name: "Nom",
    certificate_type: "Type de certificat",
    aptitude_opinion_date: "Date de l'avis d'aptitude",
    next_visit_date: "Date d'expiration",
  },
};
